import React, { useEffect, useRef, useState } from 'react';
import { Flex, Stack, Spinner, Alert, Button, } from '@stepstone-genesis/components';
import { useActiveBreakpointValue } from '@stepstone-genesis/hooks';
import MetaHandler from './MetaHandler';
import Question from './Question';
import Response from './Response';
import LimitError from './LimitError';
import Csat from './Csat';
import MoreQuestions from './MoreQuestions';
// import { getLang } from 'client/utils/cookies';
import { useMoreQuestions } from 'modules/hub/components/AMAChat/AskMeAnything/hooks/useMoreQuestions';
import { useCsat } from 'modules/hub/components/AMAChat/AskMeAnything/hooks/useCsat';
import { trackEvent } from 'client/utils/tracking-global';
import labels from 'modules/hub/components/AMAChat/constants/labels/ama';
import { clearSessionId, getCurrentSessionId, getOrCreateTrackingConversationId, } from '../../utils/sessionStorage';
import { getCompanyIdFromUrl } from '../../utils/companyIdFromUrl';
const Conversation = ({ isLoading, isInputDisabled, messages, session, inputPopulated, timeout, onChatEnd, onClickMoreQuestions, onShowCsat, onShowMoreQuestions, onError, onStarterButtonClick, sessionExpiredRef, onSessionCleared, currentSessionId: currentSessionIdProp, }) => {
    const lang = 'de'; //getLang({ cookie: document.cookie });
    const [waitTimeElapsed, setWaitTimeElapsed] = useState(false);
    const sessionDropoutTrackedRef = useRef(false);
    const conversationId = session?.conversation?.id || '';
    const sessionToken = session?.token || '';
    const companyId = getCompanyIdFromUrl();
    // Prefer the session ID from the hook (available immediately after history load)
    // over the synchronous localStorage read, which misses the history-load case.
    const sessionId = currentSessionIdProp !== undefined
        ? currentSessionIdProp
        : getCurrentSessionId({ companyId });
    const amaConversationID = getOrCreateTrackingConversationId(companyId);
    const userMessageCount = messages.filter((m) => m.role === 'user').length;
    // Keep refs in sync so event listeners don't capture stale closures
    const amaConversationIDRef = useRef(amaConversationID);
    const userMessageCountRef = useRef(userMessageCount);
    useEffect(() => {
        amaConversationIDRef.current = amaConversationID;
    }, [amaConversationID]);
    useEffect(() => {
        userMessageCountRef.current = userMessageCount;
    }, [userMessageCount]);
    const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : 0;
    const moreQuestionsDelay = useActiveBreakpointValue([10000, 10000, 30000]);
    const { handleMoreQuestionsClick, showMoreQuestions, showCsat } = useMoreQuestions({
        delay: moreQuestionsDelay,
        messages,
        inputPopulated,
        sessionToken,
        conversationId,
        onClickMoreQuestions,
    });
    if (onShowCsat) {
        onShowCsat(showCsat);
    }
    if (onShowMoreQuestions) {
        onShowMoreQuestions(showMoreQuestions);
    }
    const { response, handleCsatClick } = useCsat({
        lang,
        messageId: lastMessageId,
        conversationId,
        sessionToken,
        onChatEnd,
    });
    useEffect(() => {
        const timer = setTimeout(() => {
            setWaitTimeElapsed(true);
        }, 1000 * timeout);
        return () => clearTimeout(timer);
    }, [timeout]);
    // Track session dropout when timeout fires with no messages loaded
    useEffect(() => {
        if (waitTimeElapsed &&
            messages.length === 0 &&
            !sessionDropoutTrackedRef.current) {
            sessionDropoutTrackedRef.current = true;
            trackEvent({ componentId: 'askMeAnything' }, {
                componentState: 'conversationEnded',
                elementActioned: 'sessionDropout',
                elementAction: 'triggered',
                elementActionValue: 'noValue',
                amaConversationID,
            });
            clearSessionId({ companyId });
            amaConversationIDRef.current = 'noValue';
            onSessionCleared?.();
        }
    }, [waitTimeElapsed, messages.length]);
    // 30-minute idle timer — resets on every new message; fires sessionDropout if user goes silent
    useEffect(() => {
        if (userMessageCount === 0)
            return;
        const idleTimer = setTimeout(() => {
            if (!sessionDropoutTrackedRef.current && !sessionExpiredRef?.current) {
                sessionDropoutTrackedRef.current = true;
                trackEvent({ componentId: 'askMeAnything' }, {
                    componentState: 'conversationEnded',
                    elementActioned: 'sessionDropout',
                    elementAction: 'triggered',
                    elementActionValue: 'idleTimeout',
                    amaConversationID: amaConversationIDRef.current,
                });
                // Do NOT clear the session here — the user may return and resume the conversation.
                // Session is only cleared on explicit new-conversation or BE-driven session expiry.
                onSessionCleared?.();
            }
        }, 30 * 60 * 1000);
        return () => clearTimeout(idleTimer);
    }, [messages.length]); // eslint-disable-line react-hooks/exhaustive-deps
    // Page navigation tracking — fires sessionDropout when user leaves mid-conversation.
    // Two scenarios are handled:
    // 1. Full page navigation (e.g. clicking a link in another microfrontend like the header banner):
    //    pagehide fires before the browser discards the page, giving the tracking request the best
    //    chance to complete before network requests are cancelled.
    // 2. SPA navigation (client-side route change unmounts this component):
    //    pagehide does NOT fire, so the useEffect cleanup calls firePageUnloadDropout directly.
    // sessionDropoutTrackedRef prevents double-firing if both happen.
    useEffect(() => {
        const firePageUnloadDropout = () => {
            if (userMessageCountRef.current > 0 &&
                !sessionDropoutTrackedRef.current &&
                !sessionExpiredRef?.current) {
                sessionDropoutTrackedRef.current = true;
                trackEvent({ componentId: 'askMeAnything' }, {
                    componentState: 'conversationEnded',
                    elementActioned: 'sessionDropout',
                    elementAction: 'triggered',
                    elementActionValue: 'pageUnload',
                    amaConversationID: amaConversationIDRef.current,
                });
                // Do NOT clear the session here — the user may return via back-navigation and resume.
                // Session is only cleared on explicit new-conversation or BE-driven session expiry.
                amaConversationIDRef.current = 'noValue';
                // No onSessionCleared call here — the page is navigating away, and a fresh
                // mount will pre-generate the next session ID via loadConversationHistory.
            }
        };
        window.addEventListener('pagehide', firePageUnloadDropout);
        return () => {
            window.removeEventListener('pagehide', firePageUnloadDropout);
            firePageUnloadDropout(); // handles SPA navigation (unmount without full page navigation)
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    /*
    If the conversation has been empty too long, something has gone wrong
    This could be:
    - failed conversation setup (standalone call)
    - Mya not replied (note: simulate slow connect and Mya is not sending its first message)
    - undiagnosed clientside issue
    */
    if (waitTimeElapsed && messages.length === 0) {
        onError();
        return (React.createElement(React.Fragment, null,
            React.createElement(Alert, { variant: "warning", sx: { cursor: 'pointer' } }, labels[lang].errorAlert),
            React.createElement(Button, { a11yLabel: labels[lang].errorTryAgain, variant: "secondary", marginTop: "spaceM", marginX: "auto", sx: { display: 'flex' }, onClick: () => {
                    window.location.href = `${window.location.href}#ama=open`;
                    window.sessionStorage.removeItem('sessions');
                    window.location.reload();
                } }, labels[lang].errorTryAgain)));
    }
    if (!(conversationId && sessionToken) || messages.length === 0) {
        return (React.createElement(Stack, { direction: "vertical", flxItems: "center" },
            React.createElement(Spinner, { size: "xl" })));
    }
    const handleCsatTracking = (id) => {
        trackEvent({
            componentId: 'askMeAnything',
            linkName: 'conversationFeedbackClick',
        }, {
            componentName: 'askMeAnything',
            elementActioned: 'conversationFeedback',
            elementAction: 'click',
            elementActionValue: String(id),
            abTestJobDiscoveryVarOne: String(userMessageCount),
            amaConversationID,
        });
    };
    const handleMoreQuestionsTracking = (moreQuestions) => {
        trackEvent({
            componentId: 'askMeAnything',
            linkName: 'continueConversationClick',
        }, {
            componentName: 'askMeAnything',
            elementActioned: 'continueConversation',
            elementAction: 'click',
            elementActionValue: moreQuestions
                ? 'continueConversation'
                : 'endConversation',
            amaConversationID,
        });
    };
    const handleStarterButtonClick = (text) => {
        trackEvent({ componentId: 'askMeAnything', linkName: 'questionSubmitted' }, {
            componentState: 'conversationStarted',
            elementActioned: 'question',
            elementAction: 'submitted',
            elementActionValue: `message${userMessageCount + 1}`,
            amaConversationID,
        });
        onStarterButtonClick?.(text);
    };
    // Adobe `elementActionValue` is tagged `message${userMessageNumber}` to keep
    // questionSubmitted and answerGenerated in lockstep. The counter is derived
    // from FE message position (not BE `turn`) so Q5 and its A always agree, even
    // when BE turn counters reset across resumed sessions.
    // Computed outside the map to keep the render function pure (no mutation during render).
    const userMessageNumbers = (() => {
        const result = [];
        let n = 0;
        for (const m of messages ?? []) {
            if (m.role === 'user')
                n++;
            result.push(n);
        }
        return result;
    })();
    return (React.createElement(Flex, { "data-testid": "conversation", sx: { width: '100%' }, flxDirection: "column", gap: "spaceL" },
        messages?.map(({ id, role, content, meta, turn, feedback }, idx) => {
            const userMessageNumber = userMessageNumbers[idx];
            const key = `${id}${idx}`;
            const isFirstResponse = idx === 1 && role == 'assistant';
            const showRatePrompt = isFirstResponse; // && !hasSeenRatingPrompt();
            // Extract feedback data from new structure or fallback to old structure
            let thumbs = meta
                ?.miscFields?.thumbs;
            const dislikeOption = meta?.miscFields?.dislikeFormOption;
            // Map feedback field to thumbs status
            if (feedback === true) {
                thumbs = 'up';
            }
            else if (feedback === false) {
                thumbs = 'down';
            }
            // Check if this message has starter buttons
            const hasStarterButtons = Array.isArray(meta?.actions) &&
                meta?.actions.some((action) => action.starterButtons);
            // Only show feedback if it's not the first message, not a beta message,
            // and doesn't have starter buttons
            const shouldShowFeedback = idx > 0 &&
                !content?.includes('Stepstone AI – Beta') &&
                !hasStarterButtons;
            if (role === 'user') {
                return React.createElement(Question, { key: key, content: content });
            }
            if (role === 'limitError') {
                return React.createElement(LimitError, { key: key, content: content });
            }
            if (['customisedAssistant', 'assistant'].includes(role)) {
                return Object.keys(meta || {}).length ? (React.createElement(Response, { messageId: id, conversationId: conversationId, key: key, showRatePrompt: showRatePrompt, showFeedback: shouldShowFeedback, content: content, sessionToken: sessionToken, thumbs: thumbs, dislikeOption: dislikeOption, role: role, lang: lang, turn: turn, userMessageNumber: userMessageNumber, sessionId: sessionId, companyId: companyId, amaConversationID: amaConversationID },
                    React.createElement(MetaHandler, { actions: meta?.actions || [], onStarterButtonClick: handleStarterButtonClick, amaConversationID: amaConversationID, disabled: isInputDisabled }))) : (React.createElement(Response, { messageId: id, conversationId: conversationId, key: key, showFeedback: shouldShowFeedback, showRatePrompt: showRatePrompt, content: content, sessionToken: sessionToken, thumbs: thumbs, dislikeOption: dislikeOption, role: role, lang: lang, turn: turn, userMessageNumber: userMessageNumber, sessionId: sessionId, companyId: companyId, amaConversationID: amaConversationID }));
            }
        }),
        (showMoreQuestions || showCsat) && (React.createElement(Response, { role: "customisedAssistant", conversationId: conversationId, sessionToken: sessionToken, lang: lang, userMessageNumber: 0 },
            showMoreQuestions && (React.createElement(MoreQuestions, { lang: lang, onClick: async ({ moreQuestions, messageId }) => {
                    await handleMoreQuestionsClick({ moreQuestions, messageId });
                    handleMoreQuestionsTracking(moreQuestions);
                }, messageId: lastMessageId })),
            showCsat && (React.createElement(Csat, { lang: lang, response: response, onClick: async ({ id }) => {
                    await handleCsatClick({ id });
                    handleCsatTracking(id);
                } })))),
        isLoading && (React.createElement(Response, { role: "customisedAssistant", conversationId: conversationId, sessionToken: sessionToken, lang: lang, userMessageNumber: 0 },
            React.createElement("div", null)))));
};
export default Conversation;
